'use client';

import { useCallback, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';

import { useHomepageSettings } from '@/hooks/use-homepage-settings';
import { getUploadedImage } from '@/lib/book-image';
import { cn } from '@/lib/utils';

export function Logo({
  href = '/',
  className,
  height = 36,
  onClick,
  prefetch = true,
}: {
  href?: string;
  className?: string;
  height?: number;
  onClick?: () => void;
  prefetch?: boolean;
}) {
  const { settings } = useHomepageSettings();
  const logo = getUploadedImage(settings?.siteLogoUrl, settings?.siteLogoAlt, 'The Untangled Life');
  const [failedLogoSrc, setFailedLogoSrc] = useState<string | null>(null);
  const logoSrc = logo?.src;
  const handleLogoError = useCallback(() => setFailedLogoSrc(logoSrc ?? null), [logoSrc]);
  const showLogoImage = Boolean(logo?.src && logo.src !== failedLogoSrc);
  const width = Math.round((716 / 192) * height);

  return (
    <Link href={href} prefetch={prefetch} aria-label="The Untangled Life" onClick={onClick} className={cn('inline-flex items-center gap-2', className)}>
      {showLogoImage && logo ? (
        <span className="relative block shrink-0" style={{ height, width }}>
          <Image
            src={logo.src}
            alt={logo.alt}
            fill
            className="object-contain"
            priority
            sizes="(max-width: 768px) 160px, 220px"
            onError={handleLogoError}
          />
        </span>
      ) : (
        <span className="font-headline text-xl font-semibold leading-none tracking-tight text-charcoal sm:text-2xl">
          The Untangled Life
        </span>
      )}
    </Link>
  );
}

export default Logo;
